import React, { useState } from 'react'

import { Typography, Row, Col, Statistic, Card, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'

import { observer } from 'mobx-react-lite'

import { Box } from 'common/components/boxes'
import { Page } from 'common/components/Page'
import { useQuery } from 'common/hooks/use-query'

import { useVendorStores } from 'vendor/hooks/use-stores'

import { FitlerByDays } from './filter_by_days'

const commonColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
  },
  {
    title: '25th Percentile',
    dataIndex: 'response_time_25',
    align: 'right',
    width: 120,
  },
  {
    title: '50th Percentile',
    dataIndex: 'response_time_50',
    align: 'right',
    width: 120,
  },
  {
    title: '75th Percentile',
    dataIndex: 'response_time_75',
    align: 'right',
    width: 120,
  },
  {
    title: 'Average',
    dataIndex: 'average_response_time',
    align: 'right',
    width: 80,
  },
  {
    title: 'Num Orders',
    dataIndex: 'num_orders',
    align: 'right',
    width: 100,
  },
] as ColumnsType

const cardBodyStyle = {
  height: 189,
  padding: 0,
  overflowY: 'auto',
} as React.CSSProperties

const DashboardPage = observer(() => {
  const { dashboardStore } = useVendorStores()

  const [dayFilter, setDayFilter] = useState(30)

  const { isLoading: isLoadingCounterStats } = useQuery(() => dashboardStore.getCounterStats(dayFilter), [dayFilter])
  const { isLoading: isLoadingRfqWinRateByCompany } = useQuery(
    () => dashboardStore.getRfqWinRateByCompany(dayFilter),
    [dayFilter],
  )
  const { isLoading: isLoadingOrderResponseTimeByCompany } = useQuery(
    () => dashboardStore.getOrderResponseTimeByCompany(dayFilter),
    [dayFilter],
  )
  const { isLoading: isLoadingOrderResponseTimeByUser } = useQuery(
    () => dashboardStore.getOrderResponseTimeByUser(dayFilter),
    [dayFilter],
  )
  const { isLoading: isLoadingQuoteResponseTimeByCmpany } = useQuery(
    () => dashboardStore.getQuoteResponseTimeByCmpany(dayFilter),
    [dayFilter],
  )
  const { isLoading: isLoadingQuoteResponseTimeByUser } = useQuery(
    () => dashboardStore.getQuoteResponseTimeByUser(dayFilter),
    [dayFilter],
  )

  const counterStats = dashboardStore.counterStats

  return (
    <Page>
      <Page.Header>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography.Title level={3}>Dashboard</Typography.Title>

          <FitlerByDays value={dayFilter} onChange={setDayFilter} />
        </Box>
      </Page.Header>

      <Page.Content>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Card>
                  <Statistic title="# Quotes" value={counterStats?.quotes_count} loading={isLoadingCounterStats} />
                </Card>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Card>
                  <Statistic title="# Orders" value={counterStats?.orders_count} loading={isLoadingCounterStats} />
                </Card>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Card>
                  <Statistic title="# Clients" value={counterStats?.customers_count} loading={isLoadingCounterStats} />
                </Card>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Card>
                  <Statistic
                    title="Median Order Response Time"
                    value={counterStats?.median_order_response_time}
                    loading={isLoadingCounterStats}
                  />
                </Card>
              </Col>
            </Row>
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Card title="RFQ Win Rate by Company" bodyStyle={cardBodyStyle}>
              <Table
                loading={isLoadingRfqWinRateByCompany}
                dataSource={[...dashboardStore.rfqWinRateByCompany]}
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'name',
                  },
                  {
                    title: 'Won',
                    dataIndex: 'won',
                    align: 'right',
                  },
                  {
                    title: 'Lost',
                    dataIndex: 'lost',
                    align: 'right',
                  },
                  {
                    title: 'Total RFQs',
                    dataIndex: 'total_rfqs',
                    align: 'right',
                  },
                  {
                    title: 'Won (%)',
                    dataIndex: 'perc_won',
                    align: 'right',
                  },
                ]}
                pagination={false}
                size="small"
                style={{ width: '100%' }}
                scroll={{ x: 640 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Card title="Order Response time by Company" bodyStyle={cardBodyStyle}>
              <Table
                loading={isLoadingOrderResponseTimeByCompany}
                dataSource={[...dashboardStore.orderResponseTimeByCompany]}
                columns={commonColumns}
                pagination={false}
                size="small"
                style={{ width: '100%' }}
                scroll={{ x: 700 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Card title="Order Response time by User" bodyStyle={cardBodyStyle}>
              <Table
                loading={isLoadingOrderResponseTimeByUser}
                dataSource={[...dashboardStore.orderResponseTimeByUser]}
                columns={commonColumns}
                pagination={false}
                size="small"
                style={{ width: '100%' }}
                scroll={{ x: 700 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Card title="Quote Response time by Company" bodyStyle={cardBodyStyle}>
              <Table
                loading={isLoadingQuoteResponseTimeByCmpany}
                dataSource={[...dashboardStore.quoteResponseTimeByCmpany]}
                columns={commonColumns}
                pagination={false}
                size="small"
                style={{ width: '100%' }}
                scroll={{ x: 700 }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <Card title="Quote Response time by User" bodyStyle={cardBodyStyle}>
              <Table
                loading={isLoadingQuoteResponseTimeByUser}
                dataSource={[...dashboardStore.quoteResponseTimeByUser]}
                columns={commonColumns}
                pagination={false}
                size="small"
                style={{ width: '100%' }}
                scroll={{ x: 640 }}
              />
            </Card>
          </Col>
        </Row>
      </Page.Content>
    </Page>
  )
})

export default DashboardPage
